import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import {
  ShieldCheck, FileSignature, Sparkles, ArrowRight, Check, X,
  HelpCircle, Zap, Stamp, Star,
  Wallet, Briefcase, Home, RotateCcw, ShieldAlert, Megaphone, Handshake,
  Quote, BadgeCheck,
} from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { SiteHeader, SiteFooter } from "@/components/SiteChrome";
import { getConfig, logVisit } from "@/lib/api";
import { Helmet } from "react-helmet-async";

const SITE_URL = process.env.REACT_APP_SITE_URL || process.env.REACT_APP_BACKEND_URL || "";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

const PROBLEMS = [
  { id: "owed-money", icon: Wallet, title: "Someone owes me money", cta: "Recover it", to: "/legal-notices/money-recovery" },
  { id: "unpaid-salary", icon: Briefcase, title: "My employer hasn't paid me", cta: "Claim my dues", to: "/legal-notices/employment" },
  { id: "landlord-tenant", icon: Home, title: "My landlord or tenant is a problem", cta: "Sort it out", to: "/legal-notices/property" },
  { id: "no-refund", icon: RotateCcw, title: "I haven't received my refund", cta: "Get my refund", to: "/legal-notice/refund-not-received-notice" },
  { id: "online-scam", icon: ShieldAlert, title: "I've been scammed online", cta: "Take action", to: "/legal-notices/cyber" },
  { id: "reputation", icon: Megaphone, title: "Someone is damaging my reputation", cta: "Send a warning", to: "/legal-notice/defamation-notice" },
  { id: "breach", icon: Handshake, title: "Someone breached our agreement", cta: "Enforce it", to: "/legal-notice/breach-of-contract-notice" },
  { id: "not-sure", icon: HelpCircle, title: "I'm not sure what notice I need", cta: "Help me choose", to: "/create", state: { preselect: "general", startStep: 1 } },
];

const FLOW_STEPS = ["Tell us what happened", "Draft prepared", "You review it", "Pay ₹249", "Advocate review", "Notice sent"];

const AVATAR_COLORS = ["bg-navy", "bg-emerald-600", "bg-amber-500", "bg-indigo-600", "bg-rose-500", "bg-sky-600"];

const REVIEWS = [
  { name: "Rahul M.", city: "Pune", rating: 5, outcome: "₹85,000 recovered", text: "Cheque bounce sorted for ₹249. Quicker than my old lawyer." },
  { name: "Sneha", city: "Bengaluru", rating: 5, outcome: "Deposit back in 6 days", text: "My landlord ignored me for four months. One advocate-stamped notice and my full deposit was back in six days — I never left my couch." },
  { name: "Aditya Verma", city: "", rating: 5, outcome: "₹40,000 invoice cleared", text: "I run a small design studio and a client had ghosted a ₹40,000 invoice for weeks. Zexo drafted the notice in minutes, a real advocate reviewed and stamped it, and the client paid within nine days. For ₹249 this is genuinely unreal value." },
  { name: "Farah K.", city: "Hyderabad", rating: 5, outcome: "Plain English", text: "Clear, firm, zero legal jargon. Loved it." },
  { name: "Manish", city: "", rating: 5, outcome: "Felt legit", text: "Was skeptical about an online legal service, but it arrives with a real advocate's stamp and a reference number. Felt every bit as legitimate as walking into a lawyer's office." },
  { name: "anon", city: "Noida", rating: 5, outcome: "F&F released", text: "employer sat on my full & final for 3 months. one notice and HR suddenly remembered my name." },
  { name: "Kavya R.", city: "", rating: 5, outcome: "Handled with care", text: "For my defamation matter they kept the wording firm but never aggressive. You can tell an advocate actually reads it before it goes out." },
  { name: "Deepak S.", city: "Delhi", rating: 4, outcome: "Saved a day off work", text: "Visiting a lawyer would've meant a full day off work and an unpredictable bill. I did the whole thing from my phone at night, tracked every step by reference number, and paid a flat ₹249. It reached the other side in two days." },
  { name: "Ritu", city: "", rating: 5, outcome: "Refund unstuck", text: "The seller kept saying 'processing' for weeks. The notice got my refund moving within a week." },
  { name: "Arjun", city: "Chennai", rating: 5, outcome: "Reviewed before paying", text: "The draft landed in minutes, I tweaked two lines myself, and only paid once I was happy. Being able to read it before paying made all the difference for me." },
  { name: "S. Iyer", city: "", rating: 5, outcome: "₹1.2L recovered", text: "Honestly the ₹249 is what made me try it — then it recovered ₹1.2 lakh from a business partner who'd been dodging me for months." },
  { name: "Pooja M.", city: "", rating: 4, outcome: "A real human replied", text: "Professional, and an actual person replied to my email query." },
];

const STEPS = [
  { icon: FileSignature, title: "Tell us what happened", body: "Answer a few plain questions. We explain what we need and why, with examples at every step." },
  { icon: Sparkles, title: "We draft it instantly", body: "Your answers become a proper legal notice in seconds. Read it and change anything in plain words." },
  { icon: Stamp, title: "An advocate reviews and take action", body: "After payment, our advocate reviews it, applies the firm's approval seal, and dispatches it for you." },
];

const FAQS = [
  { q: "Do I need to know any law to use this?", a: "Not at all. Every step explains what we need and why, with examples. We turn your plain answers into correct legal language." },
  { q: "Does a real advocate check my notice?", a: "Yes. After you pay, a qualified advocate personally reviews your notice, refines it if needed, stamps it with the firm's approval, and only then sends it." },
  { q: "Is this a legally valid notice?", a: "Yes — it's issued through our law office on our letterhead with the firm's approval seal, delivered by email with a PDF and a unique reference number." },
  { q: "What happens after it's sent?", a: "The other party gets your notice and the compliance period you set to respond. If they don't, you can escalate — reply to your email and our office will guide you." },
  { q: "Is my information private?", a: "Yes. Your details are used only to prepare and send your notice. Payments run through a secure gateway; we never store card details." },
];

function CountUp({ to, prefix = "", suffix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let raf; const start = performance.now(); const dur = 1200;
    const tick = (t) => {
      const p = Math.min((t - start) / dur, 1);
      setN(Math.floor((1 - Math.pow(1 - p, 3)) * to));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to]);
  return <span ref={ref}>{prefix}{n}{suffix}</span>;
}

export default function LandingPage() {
  const [config, setConfig] = useState({ firm_name: "Zexo Legal", price: 249 });
  const reviewsRef = useRef(null);

  useEffect(() => {
    getConfig().then(setConfig).catch((e) => console.error("Failed to load config", e));
    let vid = localStorage.getItem("zl_visitor_id");
    if (!vid) {
      vid = (crypto.randomUUID && crypto.randomUUID()) || String(Date.now()) + Math.random();
      localStorage.setItem("zl_visitor_id", vid);
    }
    if (!sessionStorage.getItem("zl_visited")) {
      sessionStorage.setItem("zl_visited", "1");
      logVisit(vid, window.location.pathname);
    }
  }, []);

  useEffect(() => {
    const el = reviewsRef.current;
    if (!el) return;
    let raf, paused = false;
    const step = () => {
      if (!paused) {
        el.scrollLeft += 0.5;
        const half = el.scrollWidth / 2;
        if (half > 0 && el.scrollLeft >= half) el.scrollLeft -= half;
      }
      raf = requestAnimationFrame(step);
    };
    const pause = () => { paused = true; };
    const resume = () => { paused = false; };
    el.addEventListener("pointerenter", pause);
    el.addEventListener("pointerleave", resume);
    el.addEventListener("touchstart", pause, { passive: true });
    el.addEventListener("touchend", resume, { passive: true });
    raf = requestAnimationFrame(step);
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener("pointerenter", pause);
      el.removeEventListener("pointerleave", resume);
      el.removeEventListener("touchstart", pause);
      el.removeEventListener("touchend", resume);
    };
  }, []);

  return (
    <div className="min-h-screen bg-cream text-ink">
      <Helmet>
        <title>Zexo Legal</title>
        <meta name="description" content="Get legally sound notices drafted by AI and verified by experts. Trusted by individuals and businesses across India. Start for just ₹249." />
        <meta name="keywords" content="legal notice India, advocate verified notice, send online legal notice, online legal notice, legal notice" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org", "@type": "Organization",
          name: config.firm_name || "Zexo Legal", url: `${SITE_URL}/`,
          description: "Advocate-verified legal notice drafting and dispatch service in India.",
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org", "@type": "WebSite",
          name: config.firm_name || "Zexo Legal", url: `${SITE_URL}/`,
        })}</script>
      </Helmet>
      <SiteHeader firmName={config.firm_name} />

      {/* HERO */}
      <section className="relative overflow-hidden bg-night bg-noise text-white">
        <div className="pointer-events-none absolute -left-40 top-0 h-96 w-96 rounded-full bg-navy/30 blur-3xl" />
        <div className="pointer-events-none absolute -right-32 bottom-0 h-96 w-96 rounded-full bg-indigo-500/20 blur-3xl" />
        <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 py-20 lg:grid-cols-12 lg:px-10 lg:py-28">
          <div className="lg:col-span-8">
            <motion.span variants={fadeUp} initial="hidden" animate="show" className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-emerald-400">
              <ShieldCheck className="h-3.5 w-3.5" /> Advocate-verified before it's sent
            </motion.span>
            <motion.h1 variants={fadeUp} custom={1} initial="hidden" animate="show" className="mt-6 font-display text-4xl font-black leading-[1.05] sm:text-5xl lg:text-6xl">
              Have a legal problem?<br />
              <span className="text-navy">Start with a legal notice.</span>
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} initial="hidden" animate="show" className="mt-6 max-w-xl text-lg leading-relaxed text-slate-300">
              Tell us what happened. We'll prepare your notice, have it reviewed by an advocate, and help you
              send it.
            </motion.p>
            <motion.div variants={fadeUp} custom={3} initial="hidden" animate="show" className="mt-8 flex flex-wrap items-center gap-4">
              <Link to="/create" data-testid="hero-cta" className="group inline-flex items-center gap-2 rounded-full bg-navy px-8 py-4 text-base font-bold text-white shadow-xl shadow-navy/40 transition-transform duration-200 hover:-translate-y-0.5 active:scale-95">
                Start your notice <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Link>
              <div className="flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-5 py-3">
                <span className="text-sm text-slate-400">From</span>
                <span className="font-display text-2xl font-black text-white">₹{config.price}</span>
              </div>
            </motion.div>
            <motion.div variants={fadeUp} custom={4} initial="hidden" animate="show" className="mt-9 flex flex-wrap items-center gap-3 text-sm text-slate-300">
              <span className="inline-flex items-center gap-2 rounded-full bg-emerald-500/15 px-4 py-1.5 font-bold text-emerald-300 ring-1 ring-emerald-400/30">
                <Check className="h-4 w-4" strokeWidth={3} /> Post + Email + WhatsApp delivery
              </span>
            </motion.div>
          </div>

          {/* Notice mockup */}
          <motion.div initial={{ opacity: 0, y: 40, rotate: -2 }} animate={{ opacity: 1, y: 0, rotate: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className="lg:col-span-4">
            <motion.div animate={{ y: [0, -12, 0] }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }} className="glow-blue rounded-3xl bg-white p-7 text-ink">
              <div className="flex items-center justify-between border-b border-border pb-4">
                <div>
                  <p className="font-display text-base font-black uppercase tracking-wide text-ink">Legal Notice</p>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Without prejudice</p>
                </div>
                <span className="font-mono text-[11px] text-slate-400">ZL/2026/48213</span>
              </div>
              <div className="mt-4 space-y-1 text-[11px] leading-relaxed text-slate-500">
                <p><span className="font-semibold text-slate-700">To:</span> Mr. R. Sharma, 14 Link Road, Mumbai 400 053</p>
                <p><span className="font-semibold text-slate-700">Through:</span> Zexo Legal, Advocates</p>
                <p><span className="font-semibold text-slate-700">Date:</span> 14 June 2026</p>
              </div>
              <p className="mt-3 text-[11px] font-semibold text-ink">Re: Recovery of ₹1,25,000 due against Invoice No. 2213</p>
              <div className="mt-3 space-y-2">
                {[97, 100, 92, 100, 78].map((w, i) => (
                  <motion.div key={i} initial={{ opacity: 0, width: 0 }} animate={{ opacity: 1, width: `${w}%` }} transition={{ delay: 0.6 + i * 0.12, duration: 0.5 }} className="h-2 rounded-full bg-slate-100" />
                ))}
              </div>
              <div className="mt-5 flex items-center gap-3 rounded-2xl bg-emerald-50 px-4 py-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-500 text-white"><Stamp className="h-4 w-4" /></span>
                <div>
                  <p className="text-sm font-bold text-emerald-700">Reviewed &amp; approved</p>
                  <p className="text-xs text-emerald-600">Stamped &amp; dispatched by our advocate</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* REVIEWS MARQUEE */}
      <section className="overflow-hidden border-b border-border bg-gradient-to-b from-white to-cream py-16" data-testid="reviews-strip" aria-label="Customer reviews">
        <div className="mx-auto mb-10 max-w-7xl px-6 lg:px-10">
          <div className="flex flex-wrap items-end justify-between gap-5">
            <div>
              <p className="text-sm font-extrabold uppercase tracking-[0.18em] text-navy">Real outcomes</p>
              <h2 className="mt-2 font-display text-3xl font-black tracking-tight text-ink sm:text-4xl">Notices that actually got results.</h2>
            </div>
            <div className="flex items-center gap-3 rounded-2xl border border-border bg-white px-4 py-2.5 shadow-sm">
              <div className="flex items-center gap-0.5">
                {Array.from({ length: 5 }).map((_, s) => (
                  <Star key={s} className="h-4 w-4 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <span className="text-sm text-slate-600"><strong className="font-black text-ink">4.9</strong> from real clients</span>
            </div>
          </div>
        </div>
        <div ref={reviewsRef} data-testid="reviews-scroll" className="zl-reviews-scroll flex gap-6 overflow-x-auto px-6 pb-3 lg:px-10 [mask-image:linear-gradient(to_right,transparent,black_3%,black_97%,transparent)]">
          {[...REVIEWS, ...REVIEWS].map((r, i) => {
            const idx = i % REVIEWS.length;
            return (
              <figure key={i} data-testid={i < REVIEWS.length ? `review-card-${i}` : undefined}
                className="group relative flex w-[320px] shrink-0 flex-col rounded-3xl border border-slate-200/70 bg-white p-6 shadow-[0_10px_40px_-16px_rgba(20,58,158,0.22)] transition-transform duration-300 hover:-translate-y-1.5 sm:w-[380px]">
                <Quote className="pointer-events-none absolute right-5 top-5 h-10 w-10 text-navy/[0.07]" fill="currentColor" />
                <div className="flex items-center gap-0.5">
                  {Array.from({ length: 5 }).map((_, s) => (
                    <Star key={s} className={`h-4 w-4 ${s < r.rating ? "fill-amber-400 text-amber-400" : "fill-slate-200 text-slate-200"}`} />
                  ))}
                </div>
                <blockquote className="mt-4 flex-1 text-[15px] leading-relaxed text-slate-700">“{r.text}”</blockquote>
                <figcaption className="mt-6 flex items-center gap-3 border-t border-slate-100 pt-4">
                  <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-black text-white ${AVATAR_COLORS[idx % AVATAR_COLORS.length]}`}>
                    {r.name.charAt(0).toUpperCase()}
                  </span>
                  <span className="min-w-0">
                    <span className="flex items-center gap-1 text-sm font-bold text-ink">
                      {r.name} <BadgeCheck className="h-4 w-4 shrink-0 text-navy" fill="currentColor" stroke="white" />
                    </span>
                    <span className="block text-xs text-slate-400">{r.city ? `${r.city} · ` : ""}Verified client</span>
                  </span>
                </figcaption>
              </figure>
            );
          })}
        </div>
      </section>

      {/* STAT BAND */}
      <section className="bg-navy text-white">
        <div className="mx-auto grid max-w-7xl grid-cols-1 divide-x divide-white/15 sm:grid-cols-3">
          {[
            { v: <>From ₹<CountUp to={config.price} /></>, l: "Per notice" },
            { v: <>All</>, l: "Notice types covered" },
            { v: <><CountUp to={5} /> Minutes</>, l: "Ready to send in" },
          ].map((s, i) => (
            <div key={i} className="px-4 py-8 text-center">
              <p className="font-display text-3xl font-black sm:text-4xl">{s.v}</p>
              <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-blue-100">{s.l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* WHAT HAPPENED */}
      <section id="types" className="mx-auto max-w-7xl px-6 py-24 lg:px-10">
        <div className="max-w-2xl">
          <p className="text-sm font-extrabold uppercase tracking-[0.18em] text-navy">Start here</p>
          <h2 className="mt-3 font-display text-4xl font-black tracking-tight text-ink sm:text-5xl">What happened?</h2>
          <p className="mt-4 text-lg text-slate-600">Tell us what you're dealing with. We'll help you find the right legal notice — no legal terms needed.</p>
        </div>
        <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {PROBLEMS.map((p, i) => (
            <motion.div key={p.id} variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }}>
              <Link to={p.to} state={p.state} data-testid={`problem-card-${p.id}`}
                className="group flex h-full flex-col rounded-3xl border border-border bg-white p-6 transition-all duration-200 hover:-translate-y-1 hover:border-navy hover:shadow-2xl hover:shadow-navy/10">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-navy/10 text-navy transition-transform duration-200 group-hover:scale-110 group-hover:bg-navy group-hover:text-white">
                  <p.icon className="h-6 w-6" strokeWidth={2} />
                </span>
                <h3 className="mt-5 font-display text-lg font-extrabold leading-snug text-ink group-hover:text-navy">{p.title}</h3>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-navy">
                  {p.cta} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* START FOR FREE — PRICING FLOW */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-6xl px-6 lg:px-10">
          <div className="rounded-[2rem] border border-border bg-cream px-8 py-12 sm:px-12" data-testid="pricing-flow">
            <div className="max-w-2xl">
              <p className="text-sm font-extrabold uppercase tracking-[0.18em] text-navy">No upfront payment</p>
              <h2 className="mt-3 font-display text-3xl font-black tracking-tight text-ink sm:text-4xl">Start for free.<br />Pay after you review your draft.</h2>
              <p className="mt-4 text-lg text-slate-600">You only pay ₹{config.price} once you've read your draft and you're happy with it. An advocate reviews and dispatches it after that.</p>
            </div>
            <div className="zl-reviews-scroll mt-10 flex flex-nowrap items-center justify-between gap-1 overflow-x-auto pb-1">
              {FLOW_STEPS.map((s, i) => (
                <div key={s} className="flex shrink-0 items-center">
                  <span data-testid={`flow-step-${i}`}
                    className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border border-navy/15 bg-navy/5 px-2.5 py-2 text-sm font-bold text-navy">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-navy/10 text-xs text-navy">{i + 1}</span>
                    {s}
                  </span>
                  {i < FLOW_STEPS.length - 1 && <ArrowRight className="mx-0 h-4 w-4 shrink-0 text-navy/40" />}
                </div>
              ))}
            </div>
            <Link to="/create" data-testid="pricing-flow-cta"
              className="mt-10 inline-flex items-center gap-2 rounded-full bg-navy px-7 py-3.5 text-sm font-bold text-white shadow-lg shadow-navy/30 transition-transform duration-200 hover:-translate-y-0.5 active:scale-95">
              Start for free <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* COMPARISON */}
      <section className="bg-white py-24">
        <div className="mx-auto max-w-6xl px-6 lg:px-10">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-sm font-extrabold uppercase tracking-[0.18em] text-navy">Same legal power</p>
            <h2 className="mt-3 font-display text-4xl font-black tracking-tight text-ink sm:text-5xl">Why the old way costs you more.</h2>
          </div>
          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2">
            <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="rounded-3xl border border-border bg-slate-50 p-8">
              <p className="font-display text-xl font-extrabold text-slate-500">The traditional way</p>
              <ul className="mt-6 space-y-4">
                {["Days of back-and-forth and office visits", "High, unpredictable fees", "Costs ₹2,000 – 10,000", "Endless calls and follow-ups", "No visibility on progress"].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-slate-600">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-rose-100 text-rose-500"><X className="h-3.5 w-3.5" strokeWidth={3} /></span>
                    {t}
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div variants={fadeUp} custom={1} initial="hidden" whileInView="show" viewport={{ once: true }} className="relative overflow-hidden rounded-3xl border-2 border-navy bg-navy/[0.04] p-8">
              <span className="absolute right-6 top-6 rounded-full bg-navy px-3 py-1 text-[11px] font-bold text-white">From ₹{config.price}</span>
              <p className="font-display text-xl font-extrabold text-navy">With {config.firm_name}</p>
              <ul className="mt-6 space-y-4">
                {["Prepared in minutes, from anywhere", "Send Notice from ₹249", "A real advocate reviews and stamps it", "Track every step by reference number"].map((t) => (
                  <li key={t} className="flex items-start gap-3 font-semibold text-ink">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white"><Check className="h-3.5 w-3.5" strokeWidth={3} /></span>
                    {t}
                  </li>
                ))}
              </ul>
              <Link to="/create" data-testid="comparison-cta" className="mt-8 inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3 text-sm font-bold text-white transition-transform duration-200 hover:-translate-y-0.5 active:scale-95">
                Start now <ArrowRight className="h-4 w-4" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="mx-auto max-w-7xl px-6 py-24 lg:px-10">
        <div className="max-w-2xl">
          <p className="text-sm font-extrabold uppercase tracking-[0.18em] text-navy">How it works</p>
          <h2 className="mt-3 font-display text-4xl font-black tracking-tight text-ink sm:text-5xl">Three simple steps.</h2>
        </div>
        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
          {STEPS.map((s, i) => (
            <motion.div key={s.title} variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }}
              className="relative overflow-hidden rounded-3xl border border-border bg-white p-8">
              <span className="pointer-events-none absolute -right-2 -top-6 font-display text-[7rem] font-black leading-none text-navy/5">{i + 1}</span>
              <span className="relative flex h-14 w-14 items-center justify-center rounded-2xl bg-navy text-white shadow-lg shadow-navy/25">
                <s.icon className="h-6 w-6" strokeWidth={2} />
              </span>
              <h3 className="relative mt-5 font-display text-xl font-extrabold text-ink">{s.title}</h3>
              <p className="relative mt-2 text-sm leading-relaxed text-slate-600">{s.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 py-24 lg:grid-cols-12 lg:px-10">
        <div className="lg:col-span-4">
          <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-navy text-white"><HelpCircle className="h-6 w-6" strokeWidth={2} /></span>
          <h2 className="mt-5 font-display text-4xl font-black tracking-tight text-ink sm:text-5xl">Questions, answered.</h2>
          <p className="mt-4 text-slate-600">Everything you might wonder before you start. You won't be charged until you approve your draft.</p>
        </div>
        <div className="lg:col-span-8">
          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-border">
                <AccordionTrigger data-testid={`faq-q-${i}`} className="py-5 text-left font-display text-lg font-bold text-ink hover:text-navy hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[15px] leading-relaxed text-slate-600">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="px-6 pb-24 lg:px-10">
        <div className="mx-auto max-w-6xl overflow-hidden rounded-[2rem] bg-night bg-noise px-8 py-16 text-center text-white sm:py-20">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-navy text-white"><Zap className="h-6 w-6" fill="currentColor" /></span>
          <h2 className="mx-auto mt-6 max-w-2xl font-display text-4xl font-black sm:text-5xl">Ready to make it official?</h2>
          <p className="mx-auto mt-5 max-w-xl text-slate-300">It's free to start. Answer a few questions, read your draft, and pay just ₹{config.price} to have an advocate check and send it.</p>
          <Link to="/create" data-testid="final-cta" className="mt-9 inline-flex items-center gap-2 rounded-full bg-navy px-8 py-4 text-base font-bold text-white shadow-xl shadow-navy/40 transition-transform duration-200 hover:-translate-y-0.5 active:scale-95">
            Start your legal notice <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </section>

      <SiteFooter firmName={config.firm_name} />
    </div>
  );
}
